/**
 * Single source of truth for the colour of a tracker radio channel, shared by the live maps,
 * the position history table and the connectivity chips. Before 2026-09-11 four tables
 * coexisted (the history table drew BLE in blue and LoRa in green, the maps in emerald and
 * amber) and `_4G`, the spelling produced by the OpenAPI generator, was normalised three
 * different ways.
 *
 * Hues are Tailwind's: 4G violet, ALGO blue, BLE emerald, LoRa amber. The `hex` values are the
 * 500 shade of each hue and feed inline styles on map markers; the class strings feed the
 * Tailwind content scanner, so keep them literal.
 */
export type ChannelKey = '4G' | 'ALGO' | 'BLE' | 'LORA';

export interface ChannelPaletteEntry {
  readonly key: ChannelKey;
  /** Short label on chips and tags. */
  readonly label: string;
  /** Legend name on the maps (operator wording, French). */
  readonly name: string;
  /** 500 shade, for inline `style.color` / `background-color` on markers. */
  readonly hex: string;
  /** Solid marker background. */
  readonly marker: string;
  /** Lighter ping halo behind a marker. */
  readonly ping: string;
  /** Tinted pill, light and dark. */
  readonly pill: string;
  /** Bordered tag with `!important`, for PrimeNG tags whose own theme must be overridden. */
  readonly tag: string;
  /** Solid dot with a soft halo, for the live state of a chip. */
  readonly dot: string;
}

export const CHANNEL_PALETTE: Readonly<Record<ChannelKey, ChannelPaletteEntry>> = {
  '4G': {
    key: '4G',
    label: '4G',
    name: 'Embarquée',
    hex: '#8B5CF6',
    marker: 'bg-violet-500',
    ping: 'bg-violet-400',
    pill: 'bg-violet-100 text-violet-800 dark:bg-violet-500/20 dark:text-violet-300',
    tag: '!bg-violet-100 !text-violet-800 border !border-violet-400',
    dot: 'bg-violet-500 shadow-[0_0_0_3px_rgba(139,92,246,0.22)]',
  },
  ALGO: {
    key: 'ALGO',
    label: 'ALGO',
    name: 'Debarquée',
    hex: '#3B82F6',
    marker: 'bg-blue-500',
    ping: 'bg-blue-400',
    pill: 'bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300',
    tag: '!bg-blue-100 !text-blue-800 border !border-blue-400',
    dot: 'bg-blue-500 shadow-[0_0_0_3px_rgba(59,130,246,0.22)]',
  },
  BLE: {
    key: 'BLE',
    label: 'BLE',
    name: 'BLE',
    hex: '#10B981',
    marker: 'bg-emerald-500',
    ping: 'bg-emerald-400',
    pill: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300',
    tag: '!bg-emerald-100 !text-emerald-800 border !border-emerald-400',
    dot: 'bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.22)]',
  },
  LORA: {
    key: 'LORA',
    label: 'LoRa',
    name: 'LoRa',
    hex: '#F59E0B',
    marker: 'bg-amber-500',
    ping: 'bg-amber-400',
    pill: 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300',
    tag: '!bg-amber-100 !text-amber-800 border !border-amber-400',
    dot: 'bg-amber-500 shadow-[0_0_0_3px_rgba(245,158,11,0.22)]',
  },
};

/** Colour of a position whose source is not a known channel (`DEFAULT` selection, legacy rows). */
export const DEFAULT_CHANNEL_HEX = '#ed107d';

/** Neutral pill for an unknown source. */
export const NEUTRAL_PILL_CLASS = 'bg-gray-100 text-gray-800 dark:bg-slate-600/40 dark:text-slate-300';
export const NEUTRAL_TAG_CLASS = '!bg-gray-100 !text-gray-800 border !border-gray-400';

/**
 * Upper-cases a source and folds the generator spellings of 4G (`_4G`, `_4_G`) onto the API
 * value. Returns `''` for null/undefined so callers can use it as a map key safely.
 */
export function normalizeSource(source: string | null | undefined): string {
  if (!source) return '';
  const upper = source.toUpperCase();
  return upper === '_4G' || upper === '_4_G' ? '4G' : upper;
}

export function isChannelKey(source: string): source is ChannelKey {
  return source in CHANNEL_PALETTE;
}

/** Palette entry for any spelling of a channel, `undefined` for `DEFAULT` or unknown sources. */
export function channelPalette(source: string | null | undefined): ChannelPaletteEntry | undefined {
  const key = normalizeSource(source);
  return isChannelKey(key) ? CHANNEL_PALETTE[key] : undefined;
}
